'use client';

import { c as _c } from "react/compiler-runtime";
import './alert.scss';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useIntersection from 'react-use/lib/useIntersection';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import { TrackError } from '@sola/tracking/components/datalayer.tsx';
import { ButtonStyle, type ButtonStyleProps } from '../../atoms/button-style/button-style.tsx';
import { CloseButton } from '../../atoms/close-button/close-button.tsx';
import { LinkStyle } from '../../atoms/link-style/link-style.tsx';
import { cn } from '../../utils/helpers.ts';
import useReducedMotion from '../../utils/hooks/useReducedMotion.ts';
const alertDismiss = (setDismissed: Dispatch<SetStateAction<boolean>>, callback?: () => void) => {
  setDismissed(true);
  if (callback) {
    callback();
  }
};
export interface AlertProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title' | 'children'> {
  variant?: DisplayPurposeProps['variants'];
  title?: ReactNode;
  className?: string;
  message?: ReactNode;
  middleColumn?: ReactNode;
  footer?: ReactNode;
  /**
   * This should either be an anchor or button element that will be a child of <LinkStyle>
   * with the exception of using isFocus, which will use <ButtonStyle>
   */
  action?: JSX.Element;
  buttonAction?: JSX.Element;
  onClose?: () => void;
  noIcon?: boolean;
  dismissible?: boolean;
  closable?: boolean;
  scrollMobileOnly?: boolean;
  scrollIntoView?: boolean;
  scrollBehavior?: 'auto' | 'instant' | 'smooth';
  scrollPosition?: 'center' | 'end' | 'nearest' | 'start';
  mobileCentered?: boolean;
}

/**
 * Component for rendering alerts, which maintains its own dismissed state.
 */
export const Alert: React.FC<AlertProps> = t0 => {
  const $ = _c(68);
  const {
    variant: t1,
    className,
    title,
    message,
    middleColumn,
    footer,
    action,
    buttonAction,
    onClose,
    noIcon,
    dismissible: t2,
    closable: t3,
    scrollMobileOnly: t4,
    scrollIntoView: t5,
    scrollBehavior: t6,
    scrollPosition: t7,
    mobileCentered
  } = t0;
  const variant = t1 === undefined ? "neutral" : t1;
  const dismissible = t2 === undefined ? false : t2;
  const closable = t3 === undefined ? false : t3;
  const scrollMobileOnly = t4 === undefined ? false : t4;
  const scrollIntoView = t5 === undefined ? false : t5;
  const scrollBehavior = t6 === undefined ? "smooth" : t6;
  const scrollPosition = t7 === undefined ? "start" : t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["core"];
    $[0] = t8;
  } else {
    t8 = $[0];
  }
  const {
    t
  } = useTranslation(t8);
  const [dismissed, setDismissed] = useState(false);
  const [hasSeen, setHasSeen] = useState(false);
  const alertRef = useRef(null);
  const {
    intersectionRatio
  } = useIntersection(alertRef as React.RefObject<HTMLElement>, {
    root: null,
    rootMargin: "0px",
    threshold: 1
  }) ?? {};
  const reducedMotion = useReducedMotion();
  const hasCloseButton = closable || dismissible;
  let t9;
  if ($[1] !== hasSeen || $[2] !== intersectionRatio || $[3] !== reducedMotion || $[4] !== scrollBehavior || $[5] !== scrollIntoView || $[6] !== scrollMobileOnly || $[7] !== scrollPosition) {
    t9 = () => {
      if (!scrollIntoView || scrollMobileOnly && window.innerWidth >= 768) {
        return;
      }
      if (alertRef.current && !hasSeen && intersectionRatio !== 0) {
        alertRef.current?.scrollIntoView({
          behavior: reducedMotion ? "auto" : scrollBehavior,
          block: scrollPosition
        });
        setHasSeen(true);
      }
    };
    $[1] = hasSeen;
    $[2] = intersectionRatio;
    $[3] = reducedMotion;
    $[4] = scrollBehavior;
    $[5] = scrollIntoView;
    $[6] = scrollMobileOnly;
    $[7] = scrollPosition;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  useEffect(t9, t10);
  if (dismissed) {
    return null;
  }
  const t11 = `Alert Alert--${variant} relative flex items-start justify-between`;
  let t12;
  if ($[10] !== className || $[11] !== t11) {
    t12 = cn(t11, className);
    $[10] = className;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== message || $[14] !== title || $[15] !== variant) {
    t13 = variant === "negative" && <TrackError errorMessage={title ? title.toString() : message?.toString()} />;
    $[13] = message;
    $[14] = title;
    $[15] = variant;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== dismissible || $[18] !== hasCloseButton || $[19] !== mobileCentered) {
    t14 = cn("w-full p-comfy-md flex gap-comfy-xs items-center", {
      Alert__wrapper: dismissible,
      "pr-snug-xl": hasCloseButton,
      "flex-col md:flex-row text-center md:text-left": mobileCentered
    });
    $[17] = dismissible;
    $[18] = hasCloseButton;
    $[19] = mobileCentered;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== mobileCentered || $[22] !== noIcon || $[23] !== variant) {
    t15 = !noIcon && <div className={cn("Alert__icon md:align-start", {
      "align-start": !mobileCentered
    })}>{{
        neutral: <ArkIcon iconName="info-circle" />,
        positive: <ArkIcon iconName="check-circle" />,
        warning: <ArkIcon iconName="alert-triangle" />,
        negative: <ArkIcon iconName="alert-circle" />,
        brand: <ArkIcon iconName="info-circle" />
      }[variant]}</div>;
    $[21] = mobileCentered;
    $[22] = noIcon;
    $[23] = variant;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = !middleColumn;
  let t17;
  if ($[25] !== t16) {
    t17 = cn("Alert__content items-center gap-comfy-sm justify-between", {
      "grow-1": t16
    });
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== title) {
    t18 = title && <div className="Alert__title label-md label-potent label-neutral-amplified">{title}</div>;
    $[27] = title;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== message) {
    t19 = message && <div className="Alert__message label-md label-moderate mt-snug-md">{message}</div>;
    $[29] = message;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== footer) {
    t20 = footer && <div className="mt-comfy-sm">{footer}</div>;
    $[31] = footer;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== action || $[34] !== dismissible || $[35] !== mobileCentered || $[36] !== onClose || $[37] !== t) {
    t21 = dismissible && <div className={cn("Alert__btn-close mt-comfy-sm md:mr-comfy-sm", {
      "mr-comfy-sm": !mobileCentered || action
    })}><LinkStyle purpose="neutral" noUnderline={true} noPadding={true}><button type="button" onClick={() => alertDismiss(setDismissed, onClose)}>{t("core:actions.dismiss")}</button></LinkStyle></div>;
    $[33] = action;
    $[34] = dismissible;
    $[35] = mobileCentered;
    $[36] = onClose;
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== action) {
    t22 = action && <div className="Alert__btn-action mt-comfy-sm mr-comfy-sm"><LinkStyle noUnderline={true} noPadding={true}>{action}</LinkStyle></div>;
    $[39] = action;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t17 || $[42] !== t18 || $[43] !== t19 || $[44] !== t20 || $[45] !== t21 || $[46] !== t22) {
    t23 = <div className={t17}>{t18}{t19}{t20}{t21}{t22}</div>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== middleColumn) {
    t24 = middleColumn && <div className="grow-1">{middleColumn}</div>;
    $[48] = middleColumn;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== buttonAction || $[51] !== variant) {
    t25 = buttonAction && <ButtonStyle purpose={variant as ButtonStyleProps['purpose']} size="md" className="inline-block w-full md:w-fit h-fit">{buttonAction}</ButtonStyle>;
    $[50] = buttonAction;
    $[51] = variant;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t23 || $[56] !== t24 || $[57] !== t25) {
    t26 = <div className={t14}>{t15}{t23}{t24}{t25}</div>;
    $[53] = t14;
    $[54] = t15;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== hasCloseButton || $[60] !== onClose || $[61] !== t) {
    t27 = hasCloseButton && <CloseButton className="mt-snug-xl mr-snug-xl" onClick={() => alertDismiss(setDismissed, onClose)} aria-label={t("core:actions.dismiss")} />;
    $[59] = hasCloseButton;
    $[60] = onClose;
    $[61] = t;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t12 || $[64] !== t13 || $[65] !== t26 || $[66] !== t27) {
    t28 = <div ref={alertRef} className={t12}>{t13}{t26}{t27}</div>;
    $[63] = t12;
    $[64] = t13;
    $[65] = t26;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  return t28;
};